import {
  ApostleBase,
  ApostleFilterInput,
  ApostleSortBy
} from '@apeiron/apostle'
import { SortByItem, SortInput } from '@apeiron/common'
import {
  ApeironTitle,
  noForwardCustomProps,
  PageInfo,
  Pagination
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ApostleFilter from '@src/components/apostles/list/ApostleFilter'
import ApostleList from '@src/components/apostles/list/ApostleList'
import ListTemplate from '@src/components/layout/ListTemplate'
import SortDropdown from '@src/components/share/generic/SortDropdown'
import { ComponentBlock } from '@src/constants/componentBlock'
import { ApostleSkillsProvider } from '@src/contexts/apostle/ApostleSkillsContext'
import { ComponentBlockerProvider } from '@src/contexts/share/ComponentBlockerContext'
import * as R from 'ramda'
import { ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Filter = styled(
  ApostleFilter,
  noForwardCustomProps(['popupMode'])
)<ApostleFilterProps>`
  ${props => props.theme.breakpoints.up('res1024')} {
    height: ${props => (props.popupMode ? 'unset' : 'calc(100vh - 116px)')};
    top: 116px;
  }
  overflow: auto;
  width: 100%;
`

const Total = styled(Typography)`
  color: white;
  font-size: 24px;
  font-weight: 400;
  line-height: 28px;
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 8px 20px;
  }
`

const ListAndPageContainer = styled(Box)`
  position: relative;
  width: 100%;
  flex: 1;
  padding-top: 10px;
`

const ListContainer = styled(Box)`
  position: relative;
`

const PageContainer = styled(Box)`
  height: 50px;
  margin-top: 25px;
  padding: 0px 40px;
  ${props => props.theme.breakpoints.down('res480')} {
    padding: 0px 15px;
  }
  width: 100%;
`

const Dropdown = styled(SortDropdown<ApostleSortBy>)`
  height: 100%;
  margin-right: 12px;
`

const HeaderContainer = styled(Box)`
  flex-direction: column;
`

const StyledApeironTitle = styled(ApeironTitle)`
  margin-bottom: 10px;
`

const BottomBar = styled(Box)`
  display: flex;
  min-height: 46px;
  width: 100%;
`

const PaginationWrapper = (props: PaginationWrapperProps) => {
  const {
    children,
    pageInfo,
    handleOnNextPage,
    handleOnPreviousPage,
    handleOnSubmitPage
  } = props

  return (
    <ListAndPageContainer>
      {children}
      {pageInfo.totalPage > 0 && (
        <PageContainer>
          <Pagination
            prevDisabled={pageInfo.page === 1}
            nextDisabled={R.isNil(pageInfo.nextCursor)}
            value={pageInfo.page}
            totalPage={pageInfo.totalPage}
            onPrev={handleOnPreviousPage}
            onNext={handleOnNextPage}
            onSubmit={handleOnSubmitPage}
          />
        </PageContainer>
      )}
    </ListAndPageContainer>
  )
}

const ApostleListPagingView = <T extends ApostleBase>(props: Props<T>) => {
  const { t } = useTranslation()

  const {
    className,
    title,
    apostles,
    loading,
    pageInfo,
    popupMode,
    bottomBar,
    filterInput,
    sortInput,
    onFilterOrSortChange,
    sortByDropdownOptions,
    onPageNumberChange,
    renderCard,
    emptyMessage,
    shouldShowEmptyRecommendation,
    thirdNavBar
  } = props

  const handleOnFilterChange = useCallback(
    (filter: ApostleFilterInput, sort?: SortInput<ApostleSortBy>) => {
      onFilterOrSortChange(filter, sort)
    },
    [onFilterOrSortChange]
  )

  const handleOnPreviousPage = useCallback(async () => {
    onPageNumberChange(pageInfo.page - 1)
  }, [pageInfo.page, onPageNumberChange])

  const handleOnNextPage = useCallback(async () => {
    onPageNumberChange(pageInfo.page + 1)
  }, [pageInfo.page, onPageNumberChange])

  const handleOnSubmitPage = useCallback(
    async (page: number) => {
      onPageNumberChange(page)
    },
    [onPageNumberChange]
  )

  const handleOnSortChange = useCallback(
    (input: SortInput<ApostleSortBy>) => {
      onFilterOrSortChange(undefined, input)
    },
    [onFilterOrSortChange]
  )

  return (
    <ApostleSkillsProvider>
      <ListTemplate
        bottomBar={<>{bottomBar && <BottomBar>{bottomBar}</BottomBar>}</>}
        className={className}
        popupMode={popupMode}
        headerLeft={
          <HeaderContainer>
            {title && <StyledApeironTitle text={title} />}
            <Total>
              {t('apostle.list.total', {
                count: pageInfo.total
              })}
            </Total>
          </HeaderContainer>
        }
        filter={
          <ComponentBlockerProvider
            blockKeys={[ComponentBlock.ApostleFilterPurchaseType]}
          >
            <Filter
              allowFavorite
              filterInput={filterInput}
              popupMode={popupMode}
              sortInput={sortInput}
              onFilterChange={handleOnFilterChange}
            />
          </ComponentBlockerProvider>
        }
        sortDropDown={
          <Dropdown
            items={sortByDropdownOptions}
            value={sortInput}
            onSelectChange={handleOnSortChange}
          />
        }
        loadingContent={loading}
        mainContent={
          <PaginationWrapper
            pageInfo={pageInfo}
            handleOnNextPage={handleOnNextPage}
            handleOnPreviousPage={handleOnPreviousPage}
            handleOnSubmitPage={handleOnSubmitPage}
          >
            <ListContainer>
              <ApostleList
                emptyDescription={emptyMessage || ''}
                shouldShowEmptyRecommendation={shouldShowEmptyRecommendation}
                loading={loading}
                apostles={apostles}
                renderCard={renderCard}
              />
            </ListContainer>
          </PaginationWrapper>
        }
        thirdNavBar={thirdNavBar}
      />
    </ApostleSkillsProvider>
  )
}

type ApostleFilterProps = {
  popupMode?: boolean
}

type PaginationWrapperProps = {
  children: ReactNode
  pageInfo: PageInfo
  handleOnNextPage: () => Promise<void>
  handleOnPreviousPage: () => Promise<void>
  handleOnSubmitPage: (page: number) => Promise<void>
}

type Props<T> = {
  title?: string
  apostles: T[]
  loading: boolean
  pageInfo: PageInfo
  popupMode?: boolean
  bottomBar?: ReactNode
  filterInput: ApostleFilterInput
  sortInput: SortInput<ApostleSortBy>
  onFilterOrSortChange: (
    filter?: ApostleFilterInput,
    sort?: SortInput<ApostleSortBy>
  ) => void
  onPageNumberChange: (newPage: number) => void
  sortByDropdownOptions: SortByItem<ApostleSortBy>[]
  renderCard: (apostle: T) => ReactNode
  emptyMessage?: string
  shouldShowEmptyRecommendation?: boolean
  className?: string
  thirdNavBar?: ReactNode
}

export type { Props as ApostleListPagingViewProps }

export default ApostleListPagingView
